/**
 * AR Vision Demo — Scene 8 outbound voice-call runner (shared).
 *
 * THROWAWAY DEMO CODE. Places a REAL Vapi call and streams the live transcript,
 * with a deterministic scripted fallback so the beat always looks right on
 * stage. Used by BOTH the page-mounted tool (`ArOverdueCustomerDemo`) and the
 * eager registry fallback (`registerVoiceCallTools`) so `ar_place_call` behaves
 * identically wherever it fires.
 *
 * Going live needs three things:
 *   1. the Vapi key configured on the backend (`vapi.checkAvailability`),
 *   2. a destination number (`config.ar.demo.callTo` → `voiceCall.livePhoneNumber`),
 *   3. a voice-agent config (assistant + outbound number).
 * The config id is resolved automatically from the org's first configured voice
 * agent when one isn't pinned via `config.ar.demo.voiceConfigId`, so the only
 * demo-specific setting required to go live is the number to call. If any piece
 * is missing, or the live call errors, we replay the scripted transcript.
 *
 * Delete alongside frontend/src/demo/ar.
 */

import { rpcClient } from '@/api/rpc/client';
import { getArDemoState, updateArDemoState } from './fixture-store';
import type { DemoCallTranscriptLine } from './fixture-store.types';
import {
  appendTranscriptLine,
  beginCall,
  endCall,
  setCallPhase,
  setLiveCallId,
  setTranscript,
} from './voice-call-store';

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Replay the scripted transcript on a wall-clock timer so the on-screen panel
 * looks like a live, streaming call. Deterministic — the demo's safety net.
 */
async function runScriptedCall(
  lines: DemoCallTranscriptLine[],
  summary: string,
): Promise<void> {
  setCallPhase('dialing');
  await sleep(1200);
  setCallPhase('connected');
  setTranscript([]);
  let prev = 0;
  for (const line of lines) {
    await sleep(Math.max(0, line.atMs - prev));
    prev = line.atMs;
    appendTranscriptLine(line);
  }
  await sleep(1500);
  endCall(summary);
}

/**
 * Place a real outbound call via Vapi, then hand off to the live SSE transcript
 * stream. We record the call id on the store so the call panel can subscribe to
 * `useVapiCallStream` (real-time transcript over Server-Sent Events, fed by the
 * Vapi webhook) instead of polling — polling returns the transcript in batches
 * well after the words are spoken. Throws on a failed placement so the caller
 * can fall back to the scripted replay.
 *
 * NOTE: live transcript events require the backend's Vapi webhook to be publicly
 * reachable (set API_BASE_URL to a public URL; use an ngrok tunnel locally).
 */
async function runLiveCall(opts: {
  voiceAgentConfigId: string;
  phoneNumber: string;
  firstMessage: string;
  systemPrompt?: string;
}): Promise<void> {
  setCallPhase('dialing', 'Placing live call via Vapi…');
  const { callId } = await rpcClient.integrations.vapi.placeCall({
    voiceAgentConfigId: opts.voiceAgentConfigId,
    phoneNumber: opts.phoneNumber,
    firstMessage: opts.firstMessage,
    ...(opts.systemPrompt ? { systemPrompt: opts.systemPrompt } : {}),
  });
  // Transcript, live status, and the end-of-call summary now stream into the
  // call panel via the SSE hook keyed on this id.
  setLiveCallId(callId);
  setCallPhase('connected', 'Live call connected');
}

/** Resolve the voice-agent config id: pinned demo config → org's first config. */
async function resolveVoiceAgentConfigId(
  pinned: string | null,
): Promise<string | null> {
  if (pinned) return pinned;
  try {
    const configs = await rpcClient.integrations.voiceAgentConfigs.list();
    return configs[0]?.id ?? null;
  } catch {
    return null;
  }
}

/** Is the Vapi integration configured (API key set) on the backend? */
async function isVapiConfigured(): Promise<boolean> {
  try {
    const { configured } =
      await rpcClient.integrations.vapi.checkAvailability();
    return configured;
  } catch {
    return false;
  }
}

/**
 * Run the Scene-8 call. Places a REAL Vapi call with a live transcript when all
 * three preconditions hold; otherwise — or on any failure — deterministically
 * replays the scripted transcript so the beat never breaks on stage.
 *
 * To make a fallback debuggable, this evaluates each precondition explicitly and
 * logs exactly which one(s) failed under `[ar-demo] voice-call diagnostic`:
 *   - destinationNumber: `config.ar.demo.callTo` (→ `voiceCall.livePhoneNumber`)
 *   - vapiConfigured:    backend `VAPI_API_KEY` set (`vapi.checkAvailability`)
 *   - voiceAgentConfig:  a voice-agent config (pinned, or the org's first)
 * Open the browser console after triggering the call to see the verdict.
 */
export async function runDemoVoiceCall(): Promise<void> {
  // Mark the "Call Cascade" Today's-work item actioned so it crosses out on the
  // summary, the way the bulk-statement and rebuttal beats complete theirs.
  // Placing the call (live or scripted replay) is the action; idempotent.
  updateArDemoState((s) => {
    const action = s.agentActions.find(
      (a) => a.id === 'demo-action-call-cascade',
    );
    if (action && action.status !== 'completed') {
      action.status = 'completed';
      action.updatedAt = new Date().toISOString();
    }
  });

  const { voiceCall } = getArDemoState();
  const phoneNumber = voiceCall.livePhoneNumber;

  // Evaluate all three live preconditions up front so the diagnostic can report
  // every blocker at once (not just the first short-circuit).
  const vapiConfigured = await isVapiConfigured();
  const voiceAgentConfigId = vapiConfigured
    ? await resolveVoiceAgentConfigId(voiceCall.liveVoiceAgentConfigId)
    : null;

  const blockers: string[] = [];
  if (!phoneNumber) {
    blockers.push(
      'no destination number — set config.ar.demo.callTo (E.164) in the AR module config',
    );
  }
  if (!vapiConfigured) {
    blockers.push(
      'Vapi not configured on the backend — set VAPI_API_KEY (vapi.checkAvailability returned false)',
    );
  }
  if (vapiConfigured && !voiceAgentConfigId) {
    blockers.push(
      'no voice-agent config found — create one under Settings → Organization → Voice Agent (assistant + outbound number)',
    );
  }

  if (blockers.length === 0 && phoneNumber && voiceAgentConfigId) {
    console.info('[ar-demo] voice-call diagnostic: placing LIVE Vapi call', {
      phoneNumber,
      voiceAgentConfigId,
      // If this is false, the call inherits the Vapi assistant's OWN system
      // prompt (e.g. a stock "Alex from Praxis" script) instead of our
      // collections prompt — usually a stale persisted demo state missing the
      // systemPrompt field (clear it / reset the demo to re-seed).
      hasSystemPrompt: Boolean(voiceCall.systemPrompt),
    });
    beginCall({ isLive: true, note: 'Placing live call via Vapi…' });
    try {
      await runLiveCall({
        voiceAgentConfigId,
        phoneNumber,
        firstMessage: voiceCall.firstMessage,
        systemPrompt: voiceCall.systemPrompt,
      });
      return;
    } catch (err) {
      console.warn(
        '[ar-demo] live Vapi call failed — falling back to scripted transcript',
        err,
      );
      beginCall({
        isLive: false,
        note: 'Live call unavailable — replaying recorded call.',
      });
      await runScriptedCall(
        voiceCall.scriptedTranscript,
        voiceCall.scriptedSummary,
      );
      return;
    }
  }

  console.info(
    '[ar-demo] voice-call diagnostic: replaying SCRIPTED transcript — live call blocked by:\n  • ' +
      blockers.join('\n  • '),
  );
  beginCall({ isLive: false });
  await runScriptedCall(
    voiceCall.scriptedTranscript,
    voiceCall.scriptedSummary,
  );
}
